/**
 * Session storage utilities for persisting context across the auth flow.
 * Stores EntryPoint and IdentificationMethod so they can be included in all events.
 */

import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';
import type { EntryPoint, IdentificationMethod } from './types';

const STORAGE_KEY = 'auth_analytics_context';

interface SessionContext {
  entryPoint?: EntryPoint;
  identificationMethod?: IdentificationMethod;
}

/**
 * Get the current session context from session storage.
 */
export const getSessionContext = (): SessionContext => {
  try {
    const stored = getSessionStorageItemSafely(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Set the entry point for the current session.
 * Should be called once when the user first lands on auth-app.
 */
export const setEntryPoint = (entryPoint: EntryPoint): void => {
  const context = getSessionContext();
  // Only set if not already set (preserve original entry point)
  if (!context.entryPoint) {
    context.entryPoint = entryPoint;
    setSessionStorageItemSafely(STORAGE_KEY, JSON.stringify(context));
  }
};

/**
 * Get the entry point from session storage.
 * Defaults to 'sign-in' if not set.
 */
export const getEntryPoint = (): EntryPoint => {
  return getSessionContext().entryPoint ?? 'sign-in';
};

/**
 * Set the identification method for the current session.
 * Should be called when user chooses how to authenticate (manually, SSO, etc.)
 */
export const setIdentificationMethod = (method: IdentificationMethod): void => {
  const context = getSessionContext();
  context.identificationMethod = method;
  setSessionStorageItemSafely(STORAGE_KEY, JSON.stringify(context));
};

/**
 * Get the identification method from session storage.
 */
export const getIdentificationMethod = (): IdentificationMethod | undefined => {
  return getSessionContext().identificationMethod;
};

/**
 * Clear the session context (e.g., after successful login or on logout).
 */
export const clearSessionContext = (): void => {
  removeSessionStorageItemSafely(STORAGE_KEY);
};
